import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus, Search, Filter, Download, RefreshCw, Eye, Edit3, Copy, XCircle,
  ChevronDown, FileText, TrendingUp, TrendingDown, Clock, AlertCircle,
  CheckCircle2, MoreVertical, Printer, Mail, Share2, CreditCard, FileDown
} from 'lucide-react';
import billingService from '../../../services/billingService';
import toast from 'react-hot-toast';

// ─── Constants ────────────────────────────────────────────────────────────────
const DOC_TYPES = [
  { key: 'all',              label: 'All Documents',     short: 'All' },
  { key: 'sales',            label: 'Sales Invoice',     short: 'Sales',    color: 'emerald' },
  { key: 'purchase',         label: 'Purchase Bill',     short: 'Purchase', color: 'blue' },
  { key: 'sales_return',     label: 'Sales Return',      short: 'Ret.',     color: 'orange' },
  { key: 'purchase_return',  label: 'Purchase Return',   short: 'P.Ret.',   color: 'orange' },
  { key: 'credit_note',      label: 'Credit Note',       short: 'CN',       color: 'red' },
  { key: 'debit_note',       label: 'Debit Note',        short: 'DN',       color: 'amber' },
  { key: 'quotation',        label: 'Quotation',         short: 'Quote',    color: 'purple' },
  { key: 'proforma',         label: 'Proforma Invoice',  short: 'PFI',      color: 'indigo' },
  { key: 'delivery_challan', label: 'Delivery Challan',  short: 'DC',       color: 'teal' },
  { key: 'receipt',          label: 'Receipt',           short: 'RCP',      color: 'green' },
  { key: 'payment',          label: 'Payment',           short: 'PMT',      color: 'pink' },
  { key: 'journal',          label: 'Journal',           short: 'JNL',      color: 'slate' },
];

const STATUS_MAP = {
  draft:     { label: 'Draft',       cls: 'bg-slate-100 text-slate-600' },
  saved:     { label: 'Saved',       cls: 'bg-blue-100 text-blue-700' },
  pending:   { label: 'Pending',     cls: 'bg-amber-100 text-amber-700' },
  partial:   { label: 'Partial',     cls: 'bg-orange-100 text-orange-700' },
  paid:      { label: 'Paid',        cls: 'bg-emerald-100 text-emerald-700' },
  overdue:   { label: 'Overdue',     cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled',   cls: 'bg-slate-100 text-slate-400 line-through' },
  sent:      { label: 'Sent',        cls: 'bg-cyan-100 text-cyan-700' },
  accepted:  { label: 'Approved',    cls: 'bg-emerald-100 text-emerald-700' },
};

const fmt = (n) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

// ─── Component ────────────────────────────────────────────────────────────────
export default function InvoiceRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  const [activeTab, setActiveTab]     = useState('all');
  const [billings, setBillings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [stats, setStats]             = useState({});
  const [selected, setSelected]       = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [actionMenu, setActionMenu]   = useState(null); // bill id

  const [filters, setFilters] = useState({
    search: '', startDate: '', endDate: '', paymentStatus: '', status: ''
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });

  // ── Keyboard Shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') { e.preventDefault(); navigate('/admin/billing/new/sales'); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page, limit: 50,
        ...(activeTab !== 'all' ? { billingType: activeTab } : {}),
        ...(filters.search       ? { search: filters.search }             : {}),
        ...(filters.startDate    ? { startDate: filters.startDate }       : {}),
        ...(filters.endDate      ? { endDate: filters.endDate }           : {}),
        ...(filters.paymentStatus? { paymentStatus: filters.paymentStatus}: {}),
        ...(filters.status       ? { status: filters.status }             : {}),
      };
      const [res, statsRes] = await Promise.all([
        billingService.getAllBillings(params),
        billingService.getStats()
      ]);
      setBillings(res.data || []);
      setPagination(p => ({ ...p, total: res.pagination?.total || 0, pages: res.pagination?.pages || 1 }));
      setStats(statsRes.data || {});
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, pagination.page]);

  useEffect(() => { fetchData(); }, [activeTab, filters.paymentStatus, filters.status, pagination.page]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(t);
  }, [filters.search, filters.startDate, filters.endDate]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this document?')) return;
    try {
      await billingService.cancelBilling(id, { reason: 'Cancelled by admin' });
      toast.success('Cancelled');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await billingService.duplicateBilling(id);
      toast.success(`Duplicated → ${res.data.billNumber}`);
      navigate(`/admin/billing/${res.data._id}/edit`);
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await billingService.deleteBilling(id);
      toast.success('Deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Cannot delete'); }
  };

  // ── Summary Cards ─────────────────────────────────────────────────────────
  const byType = stats.byType || [];
  const salesTotal   = byType.find(t => t._id === 'sales')?.total || 0;
  const purchTotal   = byType.find(t => t._id === 'purchase')?.total || 0;
  const byPayment    = stats.byPayment || [];
  const overdueTotal = byPayment.find(t => t._id === 'overdue')?.total || 0;
  const receivable   = byPayment.filter(t => ['pending','partial','overdue'].includes(t._id)).reduce((s,t)=>s+t.total,0);

  const summaryCards = [
    { label: 'Total Sales',    value: salesTotal,   icon: TrendingUp,    color: 'emerald' },
    { label: 'Total Purchases',value: purchTotal,   icon: TrendingDown,  color: 'blue' },
    { label: 'Receivables',    value: receivable,   icon: Clock,         color: 'amber' },
    { label: 'Overdue',        value: overdueTotal, icon: AlertCircle,   color: 'red' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight">Invoice Register</h1>
          <p className="text-xs text-slate-500 font-medium mt-0.5">{pagination.total} documents · Ctrl+N New · Ctrl+F Search</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-bold text-xs hover:bg-slate-50">
            <Filter className="w-3.5 h-3.5" /> Filters
          </button>
          <button onClick={fetchData}
            className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div className="relative">
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-700 hover:bg-slate-800 text-white font-bold text-xs">
              <Plus className="w-4 h-4" /> New Invoice
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="relative group">
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-sm">
              <Plus className="w-4 h-4" /> New
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl border border-slate-200 shadow-xl z-30 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all">
              {DOC_TYPES.filter(t => t.key !== 'all').map(t => (
                <button key={t.key} onClick={() => navigate(`/admin/billing/new/${t.key}`)}
                  className="w-full text-left px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 first:rounded-t-xl last:rounded-b-xl">
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summaryCards.map(card => (
          <div key={card.label} className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-${card.color}-50`}>
              <card.icon className={`w-4 h-4 text-${card.color}-600`} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{card.label}</p>
              <p className="text-sm font-black text-slate-900">₹{fmt(card.value)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input ref={searchRef}
            type="text" placeholder="Search bill number, party, GSTIN… (Ctrl+F)"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            className="flex-1 text-sm font-medium text-slate-900 placeholder-slate-400 outline-none bg-transparent" />
          {filters.search && (
            <button onClick={() => setFilters(f => ({ ...f, search: '' }))} className="text-slate-400 hover:text-slate-600">
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="px-4 py-3 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50/60">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">From Date</label>
              <input type="date" value={filters.startDate}
                onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                className="w-full text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">To Date</label>
              <input type="date" value={filters.endDate}
                onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                className="w-full text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Payment Status</label>
              <select value={filters.paymentStatus}
                onChange={e => setFilters(f => ({ ...f, paymentStatus: e.target.value }))}
                className="w-full text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white">
                <option value="">All</option>
                {['draft','pending','partial','paid','overdue','cancelled'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Doc Status</label>
              <select value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="w-full text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white">
                <option value="">All</option>
                {['draft','saved','sent','accepted','cancelled'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Type Tabs */}
        <div className="flex overflow-x-auto scrollbar-none border-b border-slate-100">
          {DOC_TYPES.map(t => (
            <button key={t.key}
              onClick={() => { setActiveTab(t.key); setPagination(p => ({ ...p, page: 1 })); }}
              className={`shrink-0 px-3 py-2.5 text-xs font-bold border-b-2 transition-colors whitespace-nowrap
                ${activeTab === t.key
                  ? 'border-purple-600 text-purple-700 bg-purple-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              {t.short}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider w-8">
                  <input type="checkbox"
                    checked={selected.size === billings.length && billings.length > 0}
                    onChange={e => setSelected(e.target.checked ? new Set(billings.map(b => b._id)) : new Set())}
                    className="rounded" />
                </th>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Bill No.</th>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Party</th>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Taxable</th>
                <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">GST</th>
                <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase tracking-wider">Total</th>
                <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Balance</th>
                <th className="px-4 py-2.5 text-center font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 text-center font-bold text-slate-500 uppercase tracking-wider w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 10 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-3 bg-slate-100 rounded-full" /></td>
                    ))}
                  </tr>
                ))
              ) : billings.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-slate-400 font-semibold">
                  No documents found. <button onClick={() => navigate('/admin/billing/new/sales')} className="text-purple-600 underline ml-1">Create one?</button>
                </td></tr>
              ) : billings.map(b => {
                const statusInfo = STATUS_MAP[b.paymentStatus] || STATUS_MAP.draft;
                const isOverdue = b.paymentStatus === 'overdue';
                const party = b.partyName || b.party?.name || b.customer?.name || b.vendor?.name || '—';
                const gstin = b.partyGstin || b.party?.gstin || b.customerGstin || b.vendorGstin || '';
                return (
                  <tr key={b._id}
                    className={`group hover:bg-slate-50/70 transition-colors cursor-pointer
                      ${b.status === 'cancelled' ? 'opacity-50' : ''}
                      ${isOverdue ? 'bg-red-50/30' : ''}`}
                    onClick={() => navigate(`/admin/billing/${b._id}`)}>
                    <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(b._id)}
                        onChange={e => {
                          const next = new Set(selected);
                          e.target.checked ? next.add(b._id) : next.delete(b._id);
                          setSelected(next);
                        }} className="rounded" />
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="font-mono font-bold text-purple-700">{b.billNumber || '—'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 font-medium whitespace-nowrap">{fmtDate(b.date)}</td>
                    <td className="px-4 py-2.5 max-w-[160px]">
                      <p className="font-bold text-slate-900 truncate">{party}</p>
                      {gstin && <p className="font-mono text-[10px] text-slate-400">{gstin}</p>}
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 font-bold text-[10px] uppercase">
                        {DOC_TYPES.find(t => t.key === b.billingType)?.short || b.billingType}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-700 hidden lg:table-cell">
                      ₹{fmt(b.taxableValue || b.subtotal)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-700 hidden lg:table-cell">
                      ₹{fmt(b.taxAmount)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-black text-slate-900">
                      ₹{fmt(b.totalAmount)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold hidden sm:table-cell"
                      style={{ color: (b.balanceAmount || 0) > 0 ? '#dc2626' : '#16a34a' }}>
                      ₹{fmt(b.balanceAmount)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${statusInfo.cls}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button onClick={() => setActionMenu(actionMenu === b._id ? null : b._id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {actionMenu === b._id && (
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-xl border border-slate-200 shadow-xl z-20">
                            <button onClick={() => { navigate(`/admin/billing/${b._id}`); setActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-t-xl">
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                            <button onClick={() => { navigate(`/admin/billing/${b._id}/edit`); setActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                              <Edit3 className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => { handleDuplicate(b._id); setActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                              <Copy className="w-3.5 h-3.5" /> Duplicate
                            </button>
                            {b.status !== 'cancelled' && (
                              <button onClick={() => { handleCancel(b._id); setActionMenu(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50">
                                <XCircle className="w-3.5 h-3.5" /> Cancel
                              </button>
                            )}
                            {b.status === 'draft' && (
                              <button onClick={() => { handleDelete(b._id); setActionMenu(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-b-xl">
                                <XCircle className="w-3.5 h-3.5" /> Delete Draft
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/60">
            <p className="text-xs text-slate-500 font-medium">
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </p>
            <div className="flex items-center gap-1.5">
              <button disabled={pagination.page === 1}
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 bg-white text-slate-600 disabled:opacity-40">
                Prev
              </button>
              <button disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 bg-white text-slate-600 disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close action menu */}
      {actionMenu && <div className="fixed inset-0 z-10" onClick={() => setActionMenu(null)} />}
    </div>
  );
}
